// @flow

import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { actionCreators } from '../store/Calculation';

import OptionManager from '../common/OptionManager';
import DependencyManager from '../common/DependencyManager';
import ValidationManager from "../common/ValidationManager";
import DefaultValueManager from "../common/DefaultValueManager";
import RequestDataCreator from "../common/RequestDataCreator";
import type {CalculationRequestProjectInput} from "../common/RequestTypes";
import type {Action, ProjectUiInput} from "../store/Calculation";

import CalculationPhaseInput from './CalculationPhaseInput';
import CalculationProjectInfoInput from './CalculationProjectInfoInput';
import type {PhaseState} from "./CalculationPhaseInput";
import CalculationErrorList from "./CalculationErrorList";
import './CalculationProjectInput.css';
import ReactAI from 'react-appinsights';
import AddButton from "./AddButton";

type Props = {

    optionManager: OptionManager,
    defaultValueManager: DefaultValueManager,
    dependencyManager: DependencyManager,
    validationManager: ValidationManager,

    touched: {[name:string]: boolean},
    error: {[name:string]: ?string},
    personName: string,
    projectName: string,
    organisationName: string,
    phaseStates: PhaseState[],
    
    isLoading: boolean,
    calcErrorCode: number,
    calcErrorString: string,
    
    requestCalculation: (calcInput: CalculationRequestProjectInput) => Function,
    clearCalculationResult: () => Action,
    changePhaseValues: (phaseNr: number, partialPhaseState: Object) => Action,
    addPhase: () => Action,
    removePhase: (phaseNr: number) => Action,
    setCalcParams: (params: ProjectUiInput) => Action,
    
    version: string
}

class CalculationProjectInput extends React.Component<Props> {
    
    componentDidMount() {
    }

    render() {

        const hasSavedState = localStorage.getItem('inputState') !== null && localStorage.getItem('inputStateVersion') === this.props.version;
        const restoreParametersEl = hasSavedState ? <div>
                <button type="button" className="btn btn-primary print-hide double-lined" onClick={() => this.loadStateFromLocalStorage()}>Använd parametrar från senaste beräkning</button>
            </div> : null;

        let allTechChosen = this.props.phaseStates.every(phaseState => phaseState.techniquesToCompare !== -1);

        let serverErrorEl = null;
        if(this.props.calcErrorCode)
            serverErrorEl = (<div className="alert alert-danger" role="alert">
                <span>Fel i beräkning. Prova igen eller att <a onClick={ () => window.location.reload() }>ladda om</a> sidan. Prova annars vid ett senare tillfälle och kontakta support om felet kvarstår.</span>
                {/*<span><small>{props.requestErrorCode + ": "}</small></span>*/}
                {/*<span><small>{props.requestErrorString}</small></span>*/}
            </div>);

        return (<form onSubmit={this.handleSubmit} noValidate>

            <CalculationProjectInfoInput validationManager={this.props.validationManager}
                                         
                                         touched={this.props.touched}
                                         error={this.props.error}
                                         
                                         projectName={this.props.projectName}
                                         organisationName={this.props.organisationName}
                                         personName={this.props.personName}

                                         projectNameMeta={this.meta('projectName')}
                                         organisationNameMeta={this.meta('organisationName')}
                                         personNameMeta={this.meta('personName')}
                                         
                                         clearCalculationResult={this.props.clearCalculationResult}
                                         changePhaseValues={this.props.changePhaseValues}

                                         restoreParametersEl={restoreParametersEl}
                                         
                />
            <hr className="input-section-delimiter"/>

            {this.props.phaseStates.map((phaseState, idx) => <CalculationPhaseInput key={idx}
                                                                                    phaseNr={idx + 1}
                                                                                    phaseState={phaseState}
                                                                                    totalPhaseCount={this.props.phaseStates.length}

                                                                                    clearCalculationResult={this.props.clearCalculationResult}
                                                                                    changePhaseValues={this.props.changePhaseValues}
                                                                                    removePhase={this.removePhase.bind(this)}
                                                                                    
                                                                                    optionManager={this.props.optionManager}
                                                                                    dependencyManager={this.props.dependencyManager}
                                                                                    validationManager={this.props.validationManager}
                                                                                    defaultValueManager={this.props.defaultValueManager} />)}


            {  allTechChosen && <div className="calculation-submit">
                <hr className="print-hide phase-delimiter"/>
                <CalculationErrorList totalPhaseCount={this.props.phaseStates.length} phaseNr={0} error={this.props.error}  />
                { this.props.phaseStates.map((phaseState, idx) => (<CalculationErrorList key={idx} totalPhaseCount={this.props.phaseStates.length} phaseNr={idx + 1} error={phaseState.error}  />)) }
                { serverErrorEl }
                <div className="input-section">
                    <h2 className="input-section-header"></h2>
                    <div className="input-section-content">
                        <AddButton text="Lägg till etapp" onClick={this.addPhase.bind(this)} />
                        <input type="submit" value="Beräkna" className="btn btn-primary btn-lg print-hide" disabled={ this.props.isLoading } style={{marginBottom: 30, marginLeft: 30}}/>
                    </div>
                </div>
            </div> }
        </form>);
    }

    handleSubmit = (event) => {

        event.preventDefault();

        this.saveStateToLocalStorage();

        // Validate and update validation state for all phases
        let errorsAndTouchedPerPhase = this.props.phaseStates.map(phaseState => this.props.validationManager.validateAll(phaseState));
        errorsAndTouchedPerPhase.forEach((errorsAndTouched, idx) => this.props.changePhaseValues(idx + 1, errorsAndTouched));
        
        let errorsAndTouchedProject = this.props.validationManager.validateAllText(this.getProjectUiInput());
        this.props.changePhaseValues(0, errorsAndTouchedProject);
        
        let numPhaseErrors = errorsAndTouchedPerPhase.map(errorsAndTouched => Object.keys(errorsAndTouched.error)
            .filter(errorKey => typeof errorsAndTouched.error[errorKey] === 'string').length)
            .reduce((acc, current) => acc + current, 0);
        
        let numProjectErrors = Object.keys(errorsAndTouchedProject.error)
            .filter(errorKey => typeof errorsAndTouchedProject.error[errorKey] === 'string').length;
        
        if(numPhaseErrors + numProjectErrors === 0) {
            let requestCreator = new RequestDataCreator(this.props.optionManager);
            let requestData = requestCreator.createProjectRequest(this.getProjectUiInput());
            this.props.requestCalculation(requestData);
        }
    };
    
    
    addPhase() {
        this.props.clearCalculationResult();
        this.props.addPhase();
    };
    
    removePhase(phaseNr: number) {
        this.props.clearCalculationResult();
        this.props.removePhase(phaseNr);
    };
    
    getProjectUiInput() : ProjectUiInput {
        return {
            version: this.props.version,
            error: this.props.error,
            touched: this.props.touched,
            personName: this.props.personName,
            projectName: this.props.projectName,
            organisationName: this.props.organisationName,
            phaseStates: this.props.phaseStates
        }
    }

    saveStateToLocalStorage() {;
        let stateCopy = {...this.getProjectUiInput()};
        stateCopy.phaseStates.forEach(phaseState => {phaseState.touched = {}; phaseState.error = {}});
        localStorage.setItem('inputState', JSON.stringify(stateCopy));
        localStorage.setItem('inputStateVersion', this.props.version);
    }
    
    loadStateFromLocalStorage() {
        let storedStateString = localStorage.getItem('inputState');
        if(storedStateString != null) {
            
            let storedState = JSON.parse(storedStateString);
            storedState.version = this.props.version;
            // Only load same version state
            // if(storedState.version === this.props.version) {
                this.props.setCalcParams(storedState);
                this.props.clearCalculationResult();
            // }

        }
    }

    meta(name: string) {
        if(typeof this.props.error[name] === 'string') {
            return {
                valid: false,
                touched: this.props.touched[name],
                errorMessage: this.props.error[name]
            }
        }
        else
        {
            return {
                valid: true,
                touched: this.props.touched[name] === true,
            }
        }
    }


}

export default ReactAI.withTracking(connect(
    state => state.calculation.projectUiInput,
    dispatch => bindActionCreators(actionCreators, dispatch)
)(CalculationProjectInput));



// WEBPACK FOOTER //
// ./src/components/CalculationProjectInput.js